import { NavLink } from 'react-router-dom';
import { OrganizationSwitcher, UserButton } from '@clerk/clerk-react';
import { useAppStore } from '../../stores/app-store';
import {
  LayoutDashboard,
  LayoutGrid,
  User,
  Search,
  Users,
  FileText,
  Settings,
  Shield,
  Bookmark,
  ChevronLeft,
  Briefcase,
  BarChart2
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { NotificationBell } from './NotificationBell';
import { useT } from '../../lib/i18n';
import { useCurrentUser } from '../../hooks/use-current-user';

type AccessLevel = 'all' | 'team' | 'sales' | 'admin' | 'market';

interface NavItem {
  readonly path: string;
  readonly labelKey: string;
  readonly icon: LucideIcon;
  readonly access: AccessLevel;
}

const navItems: readonly NavItem[] = [
  // Personal
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard, access: 'all' },
  { path: '/profile', labelKey: 'nav.profile', icon: User, access: 'all' },
  { path: '/assignments', labelKey: 'nav.assignments', icon: FileText, access: 'all' },
  { path: '/my-assignments', labelKey: 'nav.myAssignments', icon: Bookmark, access: 'all' },
  { path: '/matches', labelKey: 'nav.matches', icon: Search, access: 'all' },
  // Team
  { path: '/team', labelKey: 'nav.team', icon: Users, access: 'team' },
  { path: '/matrix', labelKey: 'nav.matrix', icon: LayoutGrid, access: 'team' },
  { path: '/sales', labelKey: 'nav.sales', icon: Briefcase, access: 'sales' },
  { path: '/market-insights', labelKey: 'nav.marketInsights', icon: BarChart2, access: 'market' },
  // System
  { path: '/settings', labelKey: 'nav.settings', icon: Settings, access: 'all' },
  { path: '/admin', labelKey: 'nav.admin', icon: Shield, access: 'admin' }
] as const;

export function AppSidebar() {
  const sidebarOpen = useAppStore((s) => s.sidebarOpen);
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  const tFn = useT();
  const { canAccessAdmin, canAccessSales, canAccessTeam, canAccessMarketInsights } =
    useCurrentUser();

  const visibleItems = navItems.filter((item) => {
    switch (item.access) {
      case 'admin':
        return canAccessAdmin;
      case 'sales':
        return canAccessSales;
      case 'team':
        return canAccessTeam;
      case 'market':
        return canAccessMarketInsights;
      default:
        return true;
    }
  });

  return (
    <aside
      className={`flex h-full flex-col border-r border-border bg-card transition-all duration-200 ${
        sidebarOpen ? 'w-56' : 'w-14'
      }`}
    >
      {/* Header */}
      <div className="flex h-12 items-center justify-between border-b border-border px-3">
        {sidebarOpen ? (
          <OrganizationSwitcher
            hidePersonal
            appearance={{
              elements: {
                rootBox: 'w-full',
                organizationSwitcherTrigger:
                  'w-full justify-start text-sm text-foreground [&>span]:text-foreground',
                organizationPreviewMainIdentifier: 'text-foreground',
                organizationSwitcherTriggerIcon: 'text-muted-foreground'
              }
            }}
          />
        ) : (
          <span className="mx-auto text-lg font-semibold text-primary">A</span>
        )}
        {sidebarOpen && (
          <button
            onClick={toggleSidebar}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-0.5 px-2 py-3">
        {visibleItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/dashboard'}
            className={({ isActive }) =>
              `flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                isActive
                  ? 'border-l-2 border-primary bg-secondary font-medium text-primary'
                  : 'border-l-2 border-transparent text-foreground/60 hover:bg-secondary hover:text-foreground'
              } ${!sidebarOpen ? 'justify-center px-0' : ''}`
            }
            title={!sidebarOpen ? tFn(item.labelKey) : undefined}
          >
            <item.icon className="h-[18px] w-[18px] shrink-0" />
            {sidebarOpen && <span>{tFn(item.labelKey)}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="space-y-1 border-t border-border p-2">
        <NotificationBell collapsed={!sidebarOpen} />
        <UserButton
          showName={sidebarOpen}
          appearance={{
            elements: {
              rootBox: 'w-full',
              userButtonTrigger: 'w-full justify-start',
              userButtonOuterIdentifier: 'text-foreground',
              userPreviewMainIdentifier: 'text-foreground',
              userPreviewSecondaryIdentifier: 'text-muted-foreground'
            }
          }}
        />
      </div>

      {/* Expand button when collapsed */}
      {!sidebarOpen && (
        <button
          onClick={toggleSidebar}
          className="mb-2 mx-auto rounded-md p-1 text-muted-foreground hover:text-foreground"
          aria-label="Expand sidebar"
        >
          <ChevronLeft className="h-4 w-4 rotate-180" />
        </button>
      )}
    </aside>
  );
}
